import { useLingui } from "@lingui/react/macro";
import { Trash2 } from "lucide-react";
import { observer } from "mobx-react-lite";
import { useCallback, useMemo, useState } from "react";

import { useDialog } from "../../../../components/ui-kit/dialog-provider";
import { DataModel } from "../../../../models/data-model";
import { Button } from "../../button";

export const CellDeleteButton = observer(function CellDeleteButton({
  model,
  index,
  item,
  onConfirm,
  requestConfirm,
  tooltip,
  disabled,
}: {
  model: DataModel;
  index: () => number;
  /**
   * The row being deleted. Prefer passing this over relying on the index-based
   * `model.paginatedData[index()]` lookup whenever the rendered rows aren't
   * driven by the model's own pagination (e.g. DataTable, where TanStack owns
   * sort/filter/pagination so `paginatedData` is stale and the index resolves
   * to the wrong row). When omitted, falls back to the index lookup.
   */
  item?: object;
  onConfirm: () => void;
  /**
   * Replaces the generic "delete this row?" confirmation, for rows whose
   * deletion needs more than a yes/no — the user delete-impact report, say.
   * Resolving `false` cancels the delete.
   */
  requestConfirm?: () => Promise<boolean>;
  tooltip?: string;
  disabled?: boolean;
}) {
  const { t } = useLingui();
  const validationMessage = useMemo(() => {
    const row = item ?? model.paginatedData[index()];
    const message = tooltip ?? (row ? model.validateItemDelete(row) : false);
    return message || undefined;
  }, [model, index, item, tooltip]);

  const { confirm } = useDialog();
  // Held for as long as the confirmation is on screen: a `requestConfirm` can
  // take a request to answer, and a second click would open a second dialog
  // over the first, whose promise then never settles.
  const [confirming, setConfirming] = useState(false);
  const handleClick = useCallback(async () => {
    setConfirming(true);
    let confirmed: boolean;
    try {
      confirmed = requestConfirm
        ? await requestConfirm()
        : await confirm(
            t`Are you sure you want to delete this row?`,
            undefined,
            {
              destructive: true,
            },
          );
    } finally {
      setConfirming(false);
    }

    if (confirmed) {
      onConfirm();
    }
  }, [confirm, onConfirm, requestConfirm, t]);
  const isDeleteDisabled =
    disabled || confirming || !!validationMessage || model.editingIndex === -1;

  return (
    <Button
      variant="tertiary"
      destructive
      size="sm"
      icon={Trash2}
      label={t`Delete`}
      // Why the row can't be deleted is the useful hover text; with nothing to
      // report the button carries no tooltip, like the Edit beside it.
      tooltip={validationMessage ?? null}
      disabled={isDeleteDisabled}
      onClick={handleClick}
    />
  );
});
